import { ApiFeil } from './klient';
import type { Svarverdi, Valg } from './reise';

/** Det kontrollerte svaret fra rådgiveren (hendelsen <c>ferdig</c>). */
export interface Radgiversvar {
  svar: string;
  type: 'forklaring' | 'anbefaling' | 'kan_compass' | 'pris' | 'endring';
  utfall: 'ja' | 'med_oppsett' | 'ikke_i_dag' | null;
  kilder: { id: string; tittel: string }[];
  detSjekkerVi: boolean;
  foreslattEndring: { sporsmalId: string; instans: string; sporsmalTekst: string; verdi: Svarverdi; valgte: Valg[] } | null;
  endret: string | null;
}

/** Leser hendelsene i en tekst med Server-Sent Events. Gir det som er igjen, når siste hendelse ikke er hel. */
function lesHendelser(tekst: string, hendelse: (type: string, data: unknown) => void): string {
  const deler = tekst.split('\n\n');
  const rest = deler.pop() ?? '';
  for (const del of deler) {
    let type = 'message';
    let data = '';
    for (const linje of del.split('\n')) {
      if (linje.startsWith('event: ')) type = linje.slice(7);
      else if (linje.startsWith('data: ')) data += linje.slice(6);
    }
    if (data) hendelse(type, JSON.parse(data));
  }
  return rest;
}

/**
 * «Spør oss»: sender spørsmålet, og leser svaret fra strømmen. `onTekst` får hver bit av svaret når den kommer.
 * Uten strømmet kropp (for eksempel i testene) leses hele svaret på én gang.
 */
export async function sporOss(
  inn: { sporsmal: string; sporsmalId: string | null; instans: string },
  onTekst: (bit: string) => void
): Promise<Radgiversvar> {
  let svar: Response;
  try {
    svar = await fetch('/api/reise/spor-oss', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'text/event-stream', 'Content-Type': 'application/json' },
      body: JSON.stringify(inn),
    });
  } catch {
    throw new ApiFeil(0, 'nettverk');
  }

  if (!svar.ok) {
    let kode = 'ukjent';
    try {
      const problem = await svar.json();
      if (typeof problem?.kode === 'string') kode = problem.kode;
    } catch {
      // Svaret var ikke JSON
    }
    throw new ApiFeil(svar.status, kode);
  }

  let ferdig: Radgiversvar | null = null;
  const hendelse = (type: string, data: unknown) => {
    if (type === 'tekst') onTekst((data as { tekst: string }).tekst);
    else if (type === 'ferdig') ferdig = data as Radgiversvar;
  };

  const leser = svar.body?.getReader?.();
  if (leser) {
    const dekoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { done, value } = await leser.read();
      if (done) break;
      buffer = lesHendelser(buffer + dekoder.decode(value, { stream: true }), hendelse);
    }
    lesHendelser(buffer + '\n\n', hendelse);
  } else {
    lesHendelser((await svar.text()) + '\n\n', hendelse);
  }

  if (!ferdig) throw new ApiFeil(500, 'ukjent');
  return ferdig;
}
